import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc, updateDoc, Timestamp } from "firebase/firestore";
import { toast } from "react-toastify";
import "../styles/DevisModal.css";
import "../styles/Inter.css";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

interface Invoice {
  id: string;
  companyId: string;
  opportunityType: string;
  qte: number;
  pu: number;
  montantHT: number;
  montantTTC: number;
  date: Timestamp;
  updatedAt: Timestamp;
  uid?: string;
  opportunityDate?: string;
  status?: string;
  pourcentage?: number;
  montantFactureHT?: number;
  montantFactureTTC?: number;
  tva?: number;
}

interface FactureModalProps {
  show: boolean;
  handleClose: () => void;
  initialData?: Invoice | null;
  preselectedDevis?: any | null; // Devis pré-sélectionné depuis la navigation
  companiesDocId?: string;
}

interface Opportunity {
  id: string | number;
  type: string;
  qte: number;
  pu: number;
  ttc: number;
  date?: Timestamp | Date | string | number; // Accepter différents formats de date
  createdAt?: string | Timestamp | Date; // Champ alternatif pour la date de création
}

const FactureModal: React.FC<FactureModalProps> = ({ show, handleClose, initialData, preselectedDevis, companiesDocId = "companies" }) => {
  const [companies, setCompanies] = useState<{ key: string, name: string }[]>([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
  const [selectedOpportunityIndex, setSelectedOpportunityIndex] = useState<string>("");
  const [lastInvoiceNumber, setLastInvoiceNumber] = useState(0);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [userHasSelectedOpportunity, setUserHasSelectedOpportunity] = useState(false);
  
  // Nouveaux états pour les dates
  const [creationDate, setCreationDate] = useState("");
  const [modificationDate, setModificationDate] = useState("");
  const [status, setStatus] = useState("En attente de Paiement"); // Nouveau état pour le statut
  const [pourcentage, setPourcentage] = useState<number | undefined>(undefined); // Nouveau état pour le pourcentage
  const [montantFactureHT, setMontantFactureHT] = useState<number | undefined>(undefined);
  const [montantFactureTTC, setMontantFactureTTC] = useState<number | undefined>(undefined);
  const [tva, setTva] = useState<number>(20);
  const [tvaIsCustom, setTvaIsCustom] = useState(false);

  // Réinitialiser les états quand la modale s'ouvre
  useEffect(() => {
    if (show) {
      if (initialData) {
        console.log("=== MODE ÉDITION - Données reçues ===");
        console.log("Facture à éditer:", initialData);
        console.log("Type d'opportunité:", initialData.opportunityType);
        console.log("Quantité:", initialData.qte);
        console.log("Prix unitaire:", initialData.pu);
        console.log("Montant HT:", initialData.montantHT);
        console.log("Montant TTC:", initialData.montantTTC);
        console.log("Société:", initialData.companyId);
        console.log("ID:", initialData.id);
        console.log("UID:", initialData.uid);
        console.log("Statut:", initialData.status);
        
        // Vérification de la structure des données
        if (!initialData.opportunityType || !initialData.qte || !initialData.pu) {
          console.error("ERREUR: Données de facture incomplètes!");
          console.error("opportunityType:", initialData.opportunityType);
          console.error("qte:", initialData.qte);
          console.error("pu:", initialData.pu);
        }
        
        setSelectedCompany(initialData.companyId);
        setInvoiceNumber(initialData.id);
        setUserHasSelectedOpportunity(false);
        setSelectedOpportunityIndex("");
        
        // Initialiser les dates pour l'édition
        const creationDateStr = initialData.date.toDate().toISOString().split('T')[0];
        const modificationDateStr = initialData.updatedAt.toDate().toISOString().split('T')[0];
        setCreationDate(creationDateStr);
        setModificationDate(modificationDateStr);
        setStatus(initialData.status || "En attente de Paiement"); // Initialiser le statut
        setPourcentage(initialData.pourcentage); // Initialiser le pourcentage
        setMontantFactureHT(initialData.montantFactureHT);
        setMontantFactureTTC(initialData.montantFactureTTC);
        const tvaValue = initialData.tva !== undefined ? initialData.tva : 20;
        setTva(tvaValue);
        setTvaIsCustom(tvaValue !== 20);
      } else {
        setSelectedCompany("");
        setInvoiceNumber("");
        setOpportunities([]);
        setSelectedOpportunity(null);
        setSelectedOpportunityIndex("");
        setUserHasSelectedOpportunity(false);
        
        // Réinitialiser les dates pour une nouvelle facture
        const now = new Date().toISOString().split('T')[0];
        setCreationDate(now);
        setModificationDate(now);
        setStatus("En attente de Paiement");
        setPourcentage(undefined);
        setMontantFactureHT(undefined);
        setMontantFactureTTC(undefined);
        setTva(20);
        setTvaIsCustom(false);
      }
    }
  }, [show, initialData]);

  useEffect(() => {
    const fetchCompanies = async () => {
      const docRef = doc(db, "prospects", "companies");
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) {
        const data = snapshot.data();
        const sortedCompanies = Object.entries(data)
          .filter(([key, value]: [string, any]) =>
            value && typeof value.name === 'string' && value.name.trim() !== '' &&
            Array.isArray(value.opportunities) && value.opportunities.length > 0
          )
          .map(([key, value]: [string, any]) => ({ key, name: value.name }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setCompanies(sortedCompanies);
      }
    };
    fetchCompanies();
  }, []);

  useEffect(() => {
    const fetchOpportunities = async () => {
      if (!selectedCompany) {
        setOpportunities([]);
        setSelectedOpportunity(null);
        setSelectedOpportunityIndex("");
        setUserHasSelectedOpportunity(false);
        return;
      }

      const docRef = doc(db, "prospects", "companies");
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) {
        const data = snapshot.data();
        const companyData = data[selectedCompany];
        if (companyData?.opportunities && Array.isArray(companyData.opportunities)) {
          const formattedOpportunities = companyData.opportunities.map((opp: any) => ({
            ...opp,
            id: String(opp.id),
            date: opp.date || opp.createdAt || null, // Préserver le champ date ou createdAt
            createdAt: opp.createdAt || opp.date || null // Préserver aussi createdAt
          }));

          // Debug: afficher la première opportunité pour vérifier la structure
          if (formattedOpportunities.length > 0) {
            console.log("Première opportunité chargée:", formattedOpportunities[0]);
            console.log("Champ date:", formattedOpportunities[0].date, "Type:", typeof formattedOpportunities[0].date);
            console.log("Champ createdAt:", formattedOpportunities[0].createdAt, "Type:", typeof formattedOpportunities[0].createdAt);
          }

          setOpportunities(formattedOpportunities);

          // En mode édition, essayer de trouver l'opportunité correspondante
          if (initialData && initialData.companyId === selectedCompany && !userHasSelectedOpportunity) {
            console.log("=== RECHERCHE D'OPPORTUNITÉ ===");
            console.log("Mode édition - Recherche d'opportunité correspondante");
            console.log("Opportunités disponibles:", formattedOpportunities);
            console.log("Détail des opportunités disponibles:");
            formattedOpportunities.forEach((opp: Opportunity, index: number) => {
              console.log(`Index ${index}: ${opp.type} - ${opp.qte} x ${opp.pu}€ = ${opp.ttc}€`);
            });
            console.log("Opportunité recherchée:", initialData.opportunityType);
            console.log("Données de la facture:", {
              type: initialData.opportunityType,
              qte: initialData.qte,
              pu: initialData.pu,
              montantHT: initialData.montantHT,
              montantTTC: initialData.montantTTC
            });
            
            // Priorité absolue : correspondance par type + date de l'opportunité
            let matchingOpp: Opportunity | undefined = (initialData.opportunityDate)
              ? formattedOpportunities.find((opp: Opportunity) =>
                  opp.type === initialData.opportunityType &&
                  String(opp.date) === String(initialData.opportunityDate)
                )
              : undefined;

            // Recherche EXACTE par type ET quantité ET prix (priorité maximale)
            if (!matchingOpp) matchingOpp = formattedOpportunities.find((opp: Opportunity) =>
              opp.type === initialData.opportunityType &&
              Math.abs(opp.qte - initialData.qte) < 0.01 &&
              Math.abs(opp.pu - initialData.pu) < 0.01
            );
            
            console.log("Correspondance EXACTE (type + quantité + prix) trouvée:", matchingOpp);
            
            // Si pas trouvé, essayer correspondance par type ET quantité
            if (!matchingOpp) {
              matchingOpp = formattedOpportunities.find((opp: Opportunity) =>
                opp.type === initialData.opportunityType &&
                Math.abs(opp.qte - initialData.qte) < 0.01
              );
              console.log("Correspondance par type + quantité trouvée:", matchingOpp);
            }
            
            // Si pas trouvé, essayer correspondance par type ET prix unitaire
            if (!matchingOpp) {
              matchingOpp = formattedOpportunities.find((opp: Opportunity) =>
                opp.type === initialData.opportunityType &&
                Math.abs(opp.pu - initialData.pu) < 0.01
              );
              console.log("Correspondance par type + prix unitaire trouvée:", matchingOpp);
            }
            
            // Si pas trouvé, essayer correspondance exacte par type seulement
            if (!matchingOpp) {
              matchingOpp = formattedOpportunities.find((opp: Opportunity) =>
                opp.type === initialData.opportunityType
              );
              console.log("Correspondance exacte par type seulement trouvée:", matchingOpp);
            }
            
            // Si pas trouvé, essayer correspondance flexible par type
            if (!matchingOpp) {
              matchingOpp = formattedOpportunities.find((opp: Opportunity) =>
                opp.type.toLowerCase().includes(initialData.opportunityType.toLowerCase()) ||
                initialData.opportunityType.toLowerCase().includes(opp.type.toLowerCase())
              );
              console.log("Correspondance flexible par type trouvée:", matchingOpp);
            }
            
            if (matchingOpp) {
              // Utiliser indexOf au lieu de findIndex car nous avons déjà l'objet exact
              const index = formattedOpportunities.indexOf(matchingOpp);
              console.log("Opportunité sélectionnée:", matchingOpp);
              console.log("Index sélectionné dans le dropdown:", index);
              
              // Forcer la mise à jour des états
              setTimeout(() => {
                setSelectedOpportunity(matchingOpp ?? null);
                setSelectedOpportunityIndex(index >= 0 ? index.toString() : "");
                console.log("États mis à jour - selectedOpportunityIndex:", index >= 0 ? index.toString() : "");
              }, 0);
            } else {
              // Créer une opportunité virtuelle basée sur les données exactes de la facture
              const virtualOpportunity: Opportunity = {
                id: 'virtual',
                type: initialData.opportunityType,
                qte: initialData.qte,
                pu: initialData.pu,
                ttc: initialData.montantTTC,
                date: initialData.date
              };
              setSelectedOpportunity(virtualOpportunity);
              setSelectedOpportunityIndex("virtual");
              console.log("Opportunité virtuelle créée avec les données exactes de la facture:", virtualOpportunity);
              console.log("Données de la facture:", {
                type: initialData.opportunityType,
                qte: initialData.qte,
                pu: initialData.pu,
                montantHT: initialData.montantHT,
                montantTTC: initialData.montantTTC
              });
            }
          } else if (!initialData && !userHasSelectedOpportunity) {
            // Mode ajout et utilisateur n'a pas encore fait de sélection - réinitialiser
            console.log("Mode ajout - Réinitialisation de la sélection");
            setSelectedOpportunity(null);
            setSelectedOpportunityIndex("");
          }
          // Note: Si l'utilisateur a fait une sélection manuelle, on ne la change pas
        } else {
          setOpportunities([]);
          setSelectedOpportunity(null);
          setSelectedOpportunityIndex("");
          setUserHasSelectedOpportunity(false);
        }
      } else {
        setOpportunities([]);
        setSelectedOpportunity(null);
        setSelectedOpportunityIndex("");
        setUserHasSelectedOpportunity(false);
      }
    };

    fetchOpportunities();
  }, [selectedCompany, initialData, userHasSelectedOpportunity]);

  // Pré-sélectionner le devis si il est passé en prop
  useEffect(() => {
    if (show && preselectedDevis && !initialData && companies.length > 0) {
      const devis = preselectedDevis;
      const companyId = devis.companyId;
      
      // Vérifier que la société existe
      const companyExists = companies.find(c => c.key === companyId || c.name === companyId);
      
      if (companyExists) {
        setSelectedCompany(companyExists.key);
      }
    }
  }, [show, preselectedDevis, initialData, companies]);

  // Pré-sélectionner l'opportunité correspondante au devis
  useEffect(() => {
    if (show && preselectedDevis && !initialData && selectedCompany && opportunities.length > 0 && !userHasSelectedOpportunity) {
      const devis = preselectedDevis;
      
      // Chercher l'opportunité correspondante par type
      const matchingOpp = opportunities.find((opp: Opportunity) => {
        // Correspondance par type d'opportunité
        return opp.type === devis.opportunityType;
      });
      
      if (matchingOpp) {
        const index = opportunities.indexOf(matchingOpp);
        setSelectedOpportunity(matchingOpp);
        setSelectedOpportunityIndex(index.toString());
        setUserHasSelectedOpportunity(false); // Ne pas marquer comme sélection manuelle
        
        // Pré-remplir la date de création avec la date du devis
        if (devis.date) {
          let dateValue = "";
          if (devis.date instanceof Timestamp) {
            dateValue = devis.date.toDate().toISOString().slice(0, 10);
          } else if (devis.date instanceof Date) {
            dateValue = devis.date.toISOString().slice(0, 10);
          } else if (typeof devis.date === 'string') {
            try {
              const dateObj = new Date(devis.date);
              if (!isNaN(dateObj.getTime())) {
                dateValue = dateObj.toISOString().slice(0, 10);
              }
            } catch (e) {
              console.warn("Erreur de formatage de date:", e);
            }
          }
          if (dateValue) {
            setCreationDate(dateValue);
            setModificationDate(dateValue);
          }
        }
      } else {
        // Si l'opportunité n'est pas trouvée, créer une opportunité virtuelle basée sur le devis
        const virtualOpportunity: Opportunity = {
          id: 'virtual-devis',
          type: devis.opportunityType || '',
          qte: (devis as any).qte || 1,
          pu: (devis as any).pu || (devis.amount || 0),
          ttc: devis.amount ? devis.amount * 1.2 : 0,
          date: devis.date
        };
        setSelectedOpportunity(virtualOpportunity);
        setSelectedOpportunityIndex("virtual");
        
        // Pré-remplir la date de création avec la date du devis
        if (devis.date) {
          let dateValue = "";
          if (devis.date instanceof Timestamp) {
            dateValue = devis.date.toDate().toISOString().slice(0, 10);
          } else if (devis.date instanceof Date) {
            dateValue = devis.date.toISOString().slice(0, 10);
          } else if (typeof devis.date === 'string') {
            try {
              const dateObj = new Date(devis.date);
              if (!isNaN(dateObj.getTime())) {
                dateValue = dateObj.toISOString().slice(0, 10);
              }
            } catch (e) {
              console.warn("Erreur de formatage de date:", e);
            }
          }
          if (dateValue) {
            setCreationDate(dateValue);
            setModificationDate(dateValue);
          }
        }
      }
    }
  }, [show, preselectedDevis, initialData, selectedCompany, opportunities, userHasSelectedOpportunity]);

  // Debug: Surveiller les changements de selectedOpportunityIndex
  useEffect(() => {
    console.log("=== DEBUG - Changement de selectedOpportunityIndex ===");
    console.log("selectedOpportunityIndex:", selectedOpportunityIndex);
    console.log("selectedOpportunity:", selectedOpportunity);
    console.log("opportunities.length:", opportunities.length);
  }, [selectedOpportunityIndex, selectedOpportunity, opportunities.length]);

  // Calcul automatique du Montant facturé (HT) basé sur le pourcentage et le montant HT de l'opportunité
  useEffect(() => {
    // Ne calculer que si une opportunité est sélectionnée et un pourcentage est saisi
    if (selectedOpportunity && pourcentage !== undefined && pourcentage !== null && pourcentage > 0) {
      const montantHTOpportunite = selectedOpportunity.qte * selectedOpportunity.pu;
      const montantFactureHTCalcule = montantHTOpportunite * (pourcentage / 100);
      setMontantFactureHT(montantFactureHTCalcule);
    }
    // Ne pas réinitialiser si on est en mode édition et qu'il y a déjà une valeur sauvegardée
    // (elle sera recalculée si le pourcentage ou l'opportunité change)
  }, [selectedOpportunity, pourcentage]);

  // Calcul automatique du Montant facturé (TTC) basé sur le Montant facturé (HT) et la TVA
  useEffect(() => {
    if (montantFactureHT !== undefined && montantFactureHT !== null) {
      const montantFactureTTCCalcule = montantFactureHT * (1 + tva / 100);
      setMontantFactureTTC(montantFactureTTCCalcule);
    } else {
      if (!initialData || !initialData.montantFactureTTC) {
        setMontantFactureTTC(undefined);
      }
    }
  }, [montantFactureHT, tva, initialData]);

  useEffect(() => {
    const fetchLastInvoiceNumber = async () => {
      const docRef = doc(db, "prospects", "companies");
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) {
        const data = snapshot.data();
        let maxNumber = 0;

        // Chercher dans toutes les sociétés pour trouver le plus grand numéro
        Object.entries(data).forEach(([companyId, companyData]: [string, any]) => {
          if (companyData && companyData.invoices) {
            companyData.invoices.forEach((invoice: any) => {
              const num = parseInt(String(invoice.id).replace(/\D/g, ''));
              if (!isNaN(num) && num > maxNumber) {
                maxNumber = num;
              }
            });
          }
        });
        setLastInvoiceNumber(maxNumber);
      }
    };
    if (!initialData) {
      fetchLastInvoiceNumber();
    }
  }, [initialData]);

  const handleSubmit = async () => {
    if (!selectedCompany || !selectedOpportunity) {
      toast.error("Veuillez sélectionner une société et une opportunité");
      return;
    }

    // Validation des dates
    if (!creationDate || !modificationDate) {
      toast.error("Veuillez remplir les dates de création et de modification");
      return;
    }

    try {
      const docRef = doc(db, "prospects", "companies");
      const snapshot = await getDoc(docRef);

      if (!snapshot.exists()) {
        toast.error("Erreur lors de la récupération des données");
        return;
      }

      const data = snapshot.data();
      // Utiliser la société sélectionnée pour sauvegarder les factures
      const companyData = data[selectedCompany] || {};
      const currentInvoices = companyData.invoices || [];

      let updatedInvoices;
      let successMessage;
      let fileUrl: string | undefined = undefined;

      // Upload du fichier si présent
      if (attachedFile) {
        const storage = getStorage();
        const storageRef = ref(storage, `invoices/${Date.now()}_${attachedFile.name}`);
        await uploadBytes(storageRef, attachedFile);
        fileUrl = await getDownloadURL(storageRef);
      }

      // Convertir les dates string en Timestamp
      const creationTimestamp = Timestamp.fromDate(new Date(creationDate));
      const modificationTimestamp = Timestamp.fromDate(new Date(modificationDate));

      if (initialData) {
        updatedInvoices = currentInvoices.map((invoice: Invoice) => {
          // Utiliser la même logique d'identification que dans Factures.tsx
          const isMatchingInvoice = (initialData.uid && invoice.uid) 
            ? invoice.uid === initialData.uid 
            : (invoice.id === initialData.id && 
               invoice.opportunityType === initialData.opportunityType && 
               invoice.date.toMillis() === initialData.date.toMillis());
          
          return isMatchingInvoice
            ? {
                ...invoice,
                id: invoiceNumber || invoice.id,
                companyId: selectedCompany,
                opportunityType: selectedOpportunity.type,
                ...(selectedOpportunity.date ? { opportunityDate: selectedOpportunity.date } : {}),
                qte: selectedOpportunity.qte,
                pu: selectedOpportunity.pu,
                montantHT: selectedOpportunity.qte * selectedOpportunity.pu,
                montantTTC: selectedOpportunity.ttc,
                date: creationTimestamp,
                updatedAt: modificationTimestamp,
                status: status,
                tva: tva,
                // Sauvegarder le pourcentage et les montants calculés dans Firebase
                ...(pourcentage !== undefined && pourcentage !== null ? { pourcentage } : {}),
                ...(montantFactureHT !== undefined && montantFactureHT !== null ? { montantFactureHT } : {}),
                ...(montantFactureTTC !== undefined && montantFactureTTC !== null ? { montantFactureTTC } : {}),
                ...(fileUrl ? { fileUrl } : {})
              }
            : invoice;
        });
        successMessage = "Facture mise à jour avec succès";
      } else {
        const fallbackNumber = lastInvoiceNumber + 1;
        const generatedId = `F${fallbackNumber.toString().padStart(4, '0')}`;
        const idToUse = invoiceNumber || generatedId;

        // Vérifier si le numéro existe déjà dans la société sélectionnée
        const isInvoiceNumberExists = currentInvoices.some((invoice: any) => invoice.id === idToUse);

        if (isInvoiceNumberExists) {
          toast.error("Erreur : Ce numéro de facture existe déjà. Veuillez en choisir un autre.");
          return;
        }

        const newInvoice: any = {
          id: idToUse,
          companyId: selectedCompany,
          opportunityType: selectedOpportunity.type,
          ...(selectedOpportunity.date ? { opportunityDate: selectedOpportunity.date } : {}),
          qte: selectedOpportunity.qte,
          pu: selectedOpportunity.pu,
          montantHT: selectedOpportunity.qte * selectedOpportunity.pu,
          montantTTC: selectedOpportunity.ttc,
          date: creationTimestamp,
          updatedAt: modificationTimestamp,
          status: status,
          tva: tva,
          uid: Date.now().toString() + Math.random().toString(36).substring(2, 8),
          // Sauvegarder le pourcentage et les montants calculés dans Firebase seulement s'ils sont définis
          ...(pourcentage !== undefined && pourcentage !== null ? { pourcentage } : {}),
          ...(montantFactureHT !== undefined && montantFactureHT !== null ? { montantFactureHT } : {}),
          ...(montantFactureTTC !== undefined && montantFactureTTC !== null ? { montantFactureTTC } : {}),
          ...(fileUrl ? { fileUrl } : {})
        };
        updatedInvoices = [...currentInvoices, newInvoice];
        successMessage = "Facture créée avec succès";
      }

      await updateDoc(docRef, {
        [`${selectedCompany}.invoices`]: updatedInvoices
      });

      toast.success(successMessage);
      handleClose();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de la facture:", error);
      toast.error("Erreur lors de l'enregistrement de la facture");
    }
  };

  if (!show) return null;

  return (
    <div className="devis-modal-overlay">
      <div className="devis-modal-content inter-font">
        <h3 className="inter-font">{initialData ? "Modifier la Facture" : "Nouvelle Facture"}</h3>

        <div className="form-group inter-font">
          <label>Numéro de facture</label>
          <input
            type="text"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            placeholder="Laissez vide si automatique"
            className="inter-font"
          />
        </div>

        <div className="form-group inter-font">
          <label>Société</label>
          <select
            value={selectedCompany}
            onChange={(e) => {
              console.log("Changement de société:", e.target.value);
              setSelectedCompany(e.target.value);
              // Réinitialiser l'opportunité sélectionnée quand on change de société
              setSelectedOpportunity(null);
              setSelectedOpportunityIndex("");
              setUserHasSelectedOpportunity(false);
            }}
            className="inter-font"
          >
            <option value="">Sélectionner une société</option>
            {companies.map((company) => (
              <option key={company.key} value={company.key}>
                {company.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group inter-font">
          <label>Opportunité {opportunities.length > 0 && `(${opportunities.length} disponible${opportunities.length > 1 ? 's' : ''})`}</label>
          <select
            value={selectedOpportunityIndex}
            onChange={(e) => {
              console.log("Sélection d'opportunité changée:", e.target.value);
              setUserHasSelectedOpportunity(true);
              if (e.target.value === "") {
                setSelectedOpportunity(null);
                setSelectedOpportunityIndex("");
              } else if (e.target.value === "virtual") {
                // L'opportunité virtuelle est déjà sélectionnée
                console.log("Opportunité virtuelle maintenue");
              } else {
                const index = parseInt(e.target.value);
                const opp = opportunities[index];
                console.log("Opportunité trouvée:", opp);
                setSelectedOpportunity(opp || null);
                setSelectedOpportunityIndex(index.toString());
              }
            }}
            disabled={opportunities.length === 0 && selectedOpportunityIndex !== "virtual"}
            className="inter-font"
          >
            <option value="">Sélectionner une opportunité</option>
            {/* Afficher l'opportunité virtuelle en premier si elle existe */}
            {selectedOpportunityIndex === "virtual" && selectedOpportunity && (() => {
              let dateStr = "";
              if (selectedOpportunity.date) {
                const date = selectedOpportunity.date;
                // Vérifier si c'est un Timestamp Firestore
                if (date && typeof date === 'object' && 'toDate' in date && typeof (date as any).toDate === 'function') {
                  dateStr = (date as any).toDate().toLocaleDateString("fr-FR");
                } else if (date instanceof Date) {
                  dateStr = date.toLocaleDateString("fr-FR");
                } else if (typeof date === 'string') {
                  try {
                    const dateObj = new Date(date);
                    if (!isNaN(dateObj.getTime())) {
                      dateStr = dateObj.toLocaleDateString("fr-FR");
                    }
                  } catch (e) {
                    console.warn("Erreur de formatage de date:", e);
                  }
                } else if (typeof date === 'number') {
                  dateStr = new Date(date).toLocaleDateString("fr-FR");
                }
              }
              return (
                <option value="virtual" selected>
                  {dateStr ? dateStr + " - " : ""}{selectedOpportunity.type} - {selectedOpportunity.qte} x {selectedOpportunity.pu}€ = {selectedOpportunity.ttc}€ (Données de la facture)
                </option>
              );
            })()}
            {/* Afficher les opportunités réelles */}
            {opportunities.map((opp, index) => {
              let dateStr = "";
              if (opp.date) {
                const date = opp.date;
                // Vérifier si c'est un Timestamp Firestore
                if (date && typeof date === 'object' && 'toDate' in date && typeof (date as any).toDate === 'function') {
                  dateStr = (date as any).toDate().toLocaleDateString("fr-FR");
                }
                // Si c'est une Date JavaScript
                else if (date instanceof Date) {
                  dateStr = date.toLocaleDateString("fr-FR");
                }
                // Si c'est une string ISO
                else if (typeof date === 'string') {
                  try {
                    const dateObj = new Date(date);
                    if (!isNaN(dateObj.getTime())) {
                      dateStr = dateObj.toLocaleDateString("fr-FR");
                    }
                  } catch (e) {
                    console.warn("Erreur de formatage de date:", e);
                  }
                }
                // Si c'est un nombre (timestamp en millisecondes)
                else if (typeof date === 'number') {
                  dateStr = new Date(date).toLocaleDateString("fr-FR");
                }
              }
              // Essayer aussi avec createdAt si date n'existe pas
              if (!dateStr && opp.createdAt) {
                try {
                  const createdAt = opp.createdAt;
                  if (typeof createdAt === 'string') {
                    const dateObj = new Date(createdAt);
                    if (!isNaN(dateObj.getTime())) {
                      dateStr = dateObj.toLocaleDateString("fr-FR");
                    }
                  } else if (createdAt && typeof createdAt === 'object' && 'toDate' in createdAt && typeof (createdAt as any).toDate === 'function') {
                    dateStr = (createdAt as any).toDate().toLocaleDateString("fr-FR");
                  } else if (createdAt instanceof Date) {
                    dateStr = createdAt.toLocaleDateString("fr-FR");
                  }
                } catch (e) {
                  console.warn("Erreur de formatage createdAt:", e);
                }
              }
              // Debug: afficher dans la console pour vérifier
              if (index === 0) {
                console.log("Exemple d'opportunité - date:", opp.date, "type:", typeof opp.date, "createdAt:", opp.createdAt);
              }
              return (
                <option key={index} value={index.toString()}>
                  {dateStr ? dateStr + " - " : ""}{opp.type} - {opp.qte} x {opp.pu}€ = {opp.ttc}€
                </option>
              );
            })}
          </select>
          {opportunities.length === 0 && selectedCompany && (
            <small style={{ color: '#dc3545' }}>
              Aucune opportunité trouvée pour cette société
            </small>
          )}
          {selectedOpportunity && (
            <small style={{ color: selectedOpportunityIndex === "virtual" ? '#ffc107' : '#28a745' }}>
              {selectedOpportunityIndex === "virtual" ? "⚠️ " : "✓ "}
              Opportunité sélectionnée: {selectedOpportunity.type}
              {selectedOpportunityIndex === "virtual" && " (basée sur la facture existante)"}
            </small>
          )}
        </div>

        {selectedOpportunity && (
          <div className="facture-details inter-font">
            <h4>Détails de l'opportunité sélectionnée</h4>
            <p><strong>Type:</strong> {selectedOpportunity.type}</p>
            <p><strong>Quantité:</strong> {selectedOpportunity.qte}</p>
            <p><strong>Prix unitaire:</strong> {selectedOpportunity.pu}€</p>
            <p><strong>Montant HT:</strong> {selectedOpportunity.qte * selectedOpportunity.pu}€</p>
            <p><strong>Montant TTC:</strong> {selectedOpportunity.ttc}€</p>
          </div>
        )}
        
        {opportunities.length > 0 && !selectedOpportunity && (
          <div className="facture-details inter-font" style={{ color: '#dc3545' }}>
            <p><strong>⚠️ Attention:</strong> Veuillez sélectionner une opportunité</p>
          </div>
        )}

        {/* Champ pourcentage */}
        <div className="form-group inter-font">
          <label>Pourcentage</label>
          <input
            type="number"
            value={pourcentage !== undefined && pourcentage !== null ? pourcentage : ""}
            onChange={(e) => {
              const value = e.target.value;
              setPourcentage(value === "" ? undefined : parseFloat(value));
            }}
            placeholder="Ex: 20"
            min="0"
            max="100"
            step="0.01"
            className="inter-font"
          />
        </div>

        {/* Champ TVA */}
        <div className="form-group inter-font">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', fontWeight: 'normal' }}>
            <input
              type="checkbox"
              checked={tvaIsCustom}
              onChange={(e) => {
                const checked = e.target.checked;
                setTvaIsCustom(checked);
                if (!checked) setTva(20);
              }}
              style={{ width: 'auto', cursor: 'pointer' }}
            />
            TVA différente de 20%
          </label>
          {tvaIsCustom && (
            <input
              type="number"
              value={tva}
              onChange={(e) => setTva(parseFloat(e.target.value) || 0)}
              min="0"
              max="100"
              step="0.1"
              placeholder="Ex: 0"
              className="inter-font"
              style={{ marginTop: '8px' }}
            />
          )}
          {!tvaIsCustom && (
            <small style={{ color: '#6c757d', display: 'block', marginTop: '4px' }}>TVA appliquée : 20%</small>
          )}
        </div>

        {/* Champs montants facturés sur une même ligne */}
        <div style={{ display: 'flex', gap: '15px' }}>
          <div className="form-group inter-font" style={{ flex: 1 }}>
            <label>Montant facturé (HT)</label>
            <input
              type="number"
              value={montantFactureHT !== undefined && montantFactureHT !== null ? montantFactureHT.toFixed(2) : ""}
              readOnly
              placeholder="Calculé automatiquement"
              min="0"
              step="0.01"
              className="inter-font"
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
            {selectedOpportunity && pourcentage !== undefined && pourcentage !== null && (
              <small style={{ color: '#6c757d', display: 'block', marginTop: '4px' }}>
                Calcul: {selectedOpportunity.qte * selectedOpportunity.pu}€ × {pourcentage}% = {((selectedOpportunity.qte * selectedOpportunity.pu) * (pourcentage / 100)).toFixed(2)}€
              </small>
            )}
          </div>
          <div className="form-group inter-font" style={{ flex: 1 }}>
            <label>Montant facturé (TTC)</label>
            <input
              type="number"
              value={montantFactureTTC !== undefined && montantFactureTTC !== null ? montantFactureTTC.toFixed(2) : ""}
              readOnly
              placeholder="Calculé automatiquement"
              min="0"
              step="0.01"
              className="inter-font"
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
            {montantFactureHT !== undefined && montantFactureHT !== null && (
              <small style={{ color: '#6c757d', display: 'block', marginTop: '4px' }}>
                {tva === 0
                  ? `TVA 0% — HT = TTC : ${montantFactureHT.toFixed(2)}€`
                  : `Calcul: ${montantFactureHT.toFixed(2)}€ × ${(1 + tva / 100).toFixed(2)} (TVA ${tva}%) = ${(montantFactureHT * (1 + tva / 100)).toFixed(2)}€`
                }
              </small>
            )}
          </div>
        </div>

        {/* Nouveaux champs de date */}
        <div className="form-group inter-font">
          <label>Date de création</label>
          <input
            type="date"
            value={creationDate}
            onChange={(e) => setCreationDate(e.target.value)}
            className="inter-font"
          />
        </div>

        <div className="form-group inter-font">
          <label>Date de modification</label>
          <input
            type="date"
            value={modificationDate}
            onChange={(e) => setModificationDate(e.target.value)}
            className="inter-font"
          />
        </div>

        <div className="form-group inter-font">
          <label>Statut</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="inter-font"
          >
            <option value="En attente de Paiement">En attente de Paiement</option>
            <option value="Réglée">Réglée</option>
            <option value="Gelée">Gelée</option>
          </select>
        </div>

        <div className="form-group inter-font">
          <label>Joindre un fichier</label>
          <input
            type="file"
            onChange={(e) => setAttachedFile(e.target.files ? e.target.files[0] : null)}
            className="inter-font"
          />
        </div>

        <div className="modal-actions">
          <button onClick={handleClose} className="cancel-button inter-font">
            Annuler
          </button>
          <button onClick={handleSubmit} className="submit-button inter-font">
            {initialData && initialData.id ? "Mettre à jour" : "Créer"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FactureModal;
